import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { readAsText } from "foldkit/file";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import { settle, start } from "../../api/asyncData";
import {
	listImportBatches,
	matchImport,
	pollImportJob,
	previewUndo,
	submitImportJob,
	undoImportBatch,
} from "../../api/commands/import";
import { type ApiError, toMessage } from "../../api/errors";
import {
	ImportJobSubmitRequest,
	MATCH_CHUNK,
	type MatchRow,
} from "../../api/schemas/import";
import { randomUuid } from "../../app/idGenerator";
import { type ParsedContact, normaliseEmail } from "../../import/contact";
import { parseContactFile } from "../../import/detect";
import { Dialog } from "../../widgets/dialog";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import {
	type Model,
	type ResolutionAction,
	canConfirm,
	circleIdOf,
	commitPlan,
	filteredContacts,
	initial,
	matchesOf,
	pageCount,
	reviewRows,
} from "./model";

// COMMANDS

/** The client's only text-reading Command; the bytes never leave the browser (KTD1). */
export const ReadFile = Command.define("ReadImportFile", {
	args: { file: S.instanceOf(File), generation: S.Number },
	messages: [Message.CompletedReadFile],
	execute: ({ file, generation }) =>
		readAsText(file).pipe(
			Effect.map((text) =>
				Message.CompletedReadFile({
					name: file.name,
					text: Option.some(text),
					generation,
				}),
			),
			Effect.orElseSucceed(() =>
				Message.CompletedReadFile({
					name: file.name,
					text: Option.none(),
					generation,
				}),
			),
		),
});

/** At most `MATCH_CHUNK` rows per request (KTD15); `offset` drives the next one. */
export const MatchChunk = matchImport.command(Message.CompletedMatch, {
	offset: S.Number,
});

export const NewBatchId = Command.define("NewImportBatchId", {
	args: { generation: S.Number },
	messages: [Message.CompletedBatchId],
	execute: ({ generation }) =>
		randomUuid.pipe(
			Effect.map((batchId) =>
				Message.CompletedBatchId({ batchId, generation }),
			),
		),
});

/** Transport attempts at the submit before the run is treated as stopped. */
const MAX_ATTEMPTS = 3;

/** Consecutive poll failures before the wizard stops waiting for the job. */
const MAX_POLL_FAILURES = 5;

/** How long between one status answer and the next request. */
const POLL_INTERVAL = 1000;

/**
 * Requests are spaced and retried here rather than by the caller, because the
 * server's rate limiter is global per client, not per route (KTD16).
 * ponytail: fixed spacing and exponential backoff; make it adaptive only if the
 * limiter starts rejecting a paced run.
 */
const pause = (attempt: number): number =>
	attempt === 0 ? 150 : 500 * 2 ** attempt;

/** The whole plan in one request (idempotent on `batchId`, so a retry is safe). */
export const SubmitJob = Command.define("SubmitImportJob", {
	args: {
		...ImportJobSubmitRequest.fields,
		attempt: S.Number,
		generation: S.Number,
	},
	messages: [Message.CompletedSubmit],
	execute: ({ attempt, generation, ...body }) =>
		Effect.sleep(pause(attempt)).pipe(
			Effect.andThen(submitImportJob.run(body)),
			Effect.map((result) =>
				Message.CompletedSubmit({ attempt, generation, result }),
			),
		),
});

/** One status read, a second after the previous answer. */
export const PollJob = Command.define("PollImportJob", {
	args: { batchId: S.String, attempt: S.Number, generation: S.Number },
	messages: [Message.CompletedPoll],
	execute: ({ batchId, attempt, generation }) =>
		Effect.sleep(POLL_INTERVAL).pipe(
			Effect.andThen(pollImportJob.run({ batchId })),
			Effect.map((result) =>
				Message.CompletedPoll({ attempt, generation, result }),
			),
		),
});

export const FetchBatches = listImportBatches.command(Message.CompletedBatches);

export const FetchUndoPreview = previewUndo.command(
	Message.CompletedUndoPreview,
);

/** Re-issued with the answer's `cursor` until the server says `done` (R19). */
export const UndoBatch = undoImportBatch.command(Message.CompletedUndo);

// HELPERS

type Return = PageReturn<Model, Message>;

/** A 429 is the shared rate limiter and a 5xx is the edge; both are worth another go. A plan limit is not. */
const isRetryable = (error: ApiError): boolean =>
	error._tag === "Transport" ||
	(error._tag === "Http" && (error.status === 429 || error.status >= 500));

const toMatchRow = (contact: ParsedContact): MatchRow => {
	const email = contact.emails[0];
	const normalized = email === undefined ? undefined : normaliseEmail(email);
	return {
		rowId: contact.rowId,
		...(contact.identity === undefined ? {} : { identity: contact.identity }),
		...(normalized === undefined ? {} : { emailNormalized: normalized }),
		nameKey: contact.nameKey,
	};
};

const selectedMatchRows = (model: Model): ReadonlyArray<MatchRow> =>
	model.contacts
		.filter((contact) => model.selected.has(contact.rowId))
		.map(toMatchRow);

const matchFrom = (model: Model, shared: Shared, offset: number) =>
	MatchChunk({
		rows: selectedMatchRows(model).slice(offset, offset + MATCH_CHUNK),
		offset,
		generation: shared.generation,
	});

const submitFrom = (model: Model, shared: Shared, attempt: number) =>
	SubmitJob({
		batchId: model.batchId,
		circleId: circleIdOf(model),
		source: model.source,
		expectedRows: model.rows.length,
		skippedRows: model.skippedRows,
		rows: model.rows,
		attempt,
		generation: shared.generation,
	});

const pollFrom = (model: Model, shared: Shared, attempt: number) =>
	PollJob({ batchId: model.batchId, attempt, generation: shared.generation });

/** Another go at the request in flight, `attempt` counting up. */
const again = (
	model: Model,
	attempt: number,
	command: NonNullable<Return["commands"]>,
): Return => ({
	model: evo(model, { attempt: () => attempt }),
	commands: command,
});

/** The run is over: the Summary explains why if a reason is known. */
const finished = (model: Model, reason: Option.Option<string>): Return => ({
	model: evo(model, {
		step: () => "Summary" as const,
		stoppedReason: (current) => Option.orElse(reason, () => current),
	}),
});

const toggle = (
	set: ReadonlySet<number>,
	rowId: number,
): ReadonlySet<number> => {
	const next = new Set(set);
	if (!next.delete(rowId)) next.add(rowId);
	return next;
};

const selectAll = (model: Model): ReadonlySet<number> => {
	const ids = filteredContacts(model).map((contact) => contact.rowId);
	const next = new Set(model.selected);
	const all = ids.every((id) => next.has(id));
	for (const id of ids) {
		if (all) next.delete(id);
		else next.add(id);
	}
	return next;
};

/** Applies one decision to every unresolved single-candidate row. */
const bulk = (
	model: Model,
	action: ResolutionAction,
): ReadonlyMap<
	number,
	{
		readonly action: ResolutionAction;
		readonly targetRecordId: Option.Option<string>;
	}
> => {
	const next = new Map(model.resolutions);
	for (const row of reviewRows(model)) {
		if (next.has(row.contact.rowId) || row.candidates.length !== 1) continue;
		next.set(row.contact.rowId, {
			action,
			targetRecordId:
				action === "merge"
					? Option.fromNullishOr(row.candidates[0]?.recordId)
					: Option.none(),
		});
	}
	return next;
};

const parsed = (model: Model, name: string, text: string): Model => {
	const outcome = parseContactFile(text);
	if (!outcome.ok) {
		return evo(model, {
			step: () => "Pick" as const,
			fileName: () => name,
			parseError: () =>
				Option.some({
					kind: outcome.error.kind,
					row:
						outcome.error.kind === "malformed"
							? Option.some(outcome.error.row)
							: Option.none<number>(),
				}),
		});
	}
	return evo(model, {
		step: () => "Preview" as const,
		fileName: () => name,
		source: () => outcome.format,
		contacts: () => outcome.contacts,
		excluded: () => outcome.excluded,
		parseError: () => Option.none(),
	});
};

const dialogChild = {
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
};
const foldDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	...dialogChild,
});
const openDialog = Update.foldChildStep({
	update: Dialog.open,
	...dialogChild,
});
const closeDialog = Update.foldChildStep({
	update: Dialog.close,
	...dialogChild,
});

/** The dialog's own Commands run alongside the page's own. */
const alongside = (step: Return, commands: Return["commands"]): Return => ({
	model: step.model,
	commands: [...(step.commands ?? []), ...(commands ?? [])],
});

const loadBatches = (model: Model, shared: Shared): Return => ({
	model: evo(model, { batches: (data) => start(data) }),
	commands: [FetchBatches({ generation: shared.generation })],
});

// UPDATE

export const page: Page<Model, Message> = {
	init: () => ({ model: initial() }),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared): Return =>
		Message.match<Return>(message, {
			// Re-picking from any step throws the whole wizard away.
			ChangedFile: ({ file }) =>
				Option.match(file, {
					onNone: () => ({ model }),
					onSome: (chosen) => ({
						model: initial(),
						commands: [
							ReadFile({ file: chosen, generation: shared.generation }),
						],
					}),
				}),
			CompletedReadFile: ({ name, text, generation }) => {
				if (generation !== shared.generation) return { model };
				return {
					model: Option.match(text, {
						onNone: () =>
							evo(model, {
								step: () => "Pick" as const,
								fileName: () => name,
								parseError: () =>
									Option.some({
										kind: "unreadable" as const,
										row: Option.none<number>(),
									}),
							}),
						onSome: (source) => parsed(model, name, source),
					}),
				};
			},
			ChangedFilter: ({ value }) => ({
				model: evo(model, { filter: () => value, pageIndex: () => 0 }),
			}),
			ChangedPage: ({ page: index }) => ({
				model: evo(model, {
					pageIndex: () => Math.min(Math.max(0, index), pageCount(model) - 1),
				}),
			}),
			ToggledRow: ({ rowId }) => ({
				model: evo(model, { selected: (set) => toggle(set, rowId) }),
			}),
			ToggledSelectAll: () => ({
				model: evo(model, { selected: () => selectAll(model) }),
			}),
			ClickedContinue: () => {
				if (model.step !== "Preview" || model.selected.size === 0)
					return { model };
				const started = evo(model, {
					matches: () => start(AsyncData.Idle()),
					resolutions: () => new Map(),
				});
				return {
					model: started,
					commands: [matchFrom(started, shared, 0)],
				};
			},
			ClickedRetryMatch: () => {
				if (model.step !== "Preview") return { model };
				const started = evo(model, {
					matches: () => start(AsyncData.Idle()),
				});
				return { model: started, commands: [matchFrom(started, shared, 0)] };
			},
			CompletedMatch: ({ rows, offset, generation, result }) => {
				if (generation !== shared.generation || model.step !== "Preview")
					return { model };
				if (Result.isFailure(result))
					return {
						model: evo(model, { matches: (data) => settle(data, result) }),
					};
				const response = result.success;
				const previous = matchesOf(model);
				const merged = {
					circleId: response.circleId,
					remainingRecordCapacity: response.remainingRecordCapacity,
					results:
						offset === 0
							? response.results
							: [...previous.results, ...response.results],
				};
				const next = offset + rows.length;
				if (next >= selectedMatchRows(model).length) {
					return {
						model: evo(model, {
							matches: () => AsyncData.succeed(merged),
							step: () => "Review" as const,
						}),
					};
				}
				const advanced = evo(model, {
					matches: () => start(AsyncData.succeed(merged)),
				});
				return {
					model: advanced,
					commands: [matchFrom(advanced, shared, next)],
				};
			},
			ClickedBack: () => ({
				model:
					model.step === "Review"
						? evo(model, { step: () => "Preview" as const })
						: model,
			}),
			ChoseResolution: ({ rowId, action, targetRecordId }) => ({
				model: evo(model, {
					resolutions: (current) =>
						new Map(current).set(rowId, { action, targetRecordId }),
				}),
			}),
			ClickedBulk: ({ action }) => ({
				model: evo(model, { resolutions: () => bulk(model, action) }),
			}),
			ClickedConfirm: () => {
				const plan = commitPlan(model);
				if (model.step !== "Review" || !canConfirm(model, plan))
					return { model };
				return {
					model: evo(model, {
						step: () => "Committing" as const,
						rows: () => plan.rows,
						skippedRows: () => plan.skipped,
						results: () => [],
						total: () => plan.rows.length,
						landed: () => 0,
						attempt: () => 0,
						stoppedReason: () => Option.none(),
					}),
					commands: [NewBatchId({ generation: shared.generation })],
				};
			},
			CompletedBatchId: ({ batchId, generation }) => {
				if (generation !== shared.generation || model.step !== "Committing")
					return { model };
				const named = evo(model, { batchId: () => batchId });
				return { model: named, commands: [submitFrom(named, shared, 0)] };
			},
			CompletedSubmit: ({ attempt, generation, result }) => {
				if (generation !== shared.generation || model.step !== "Committing")
					return { model };
				if (attempt !== model.attempt) return { model };
				if (Result.isFailure(result)) {
					const error = result.failure;
					return isRetryable(error) && attempt + 1 < MAX_ATTEMPTS
						? again(model, attempt + 1, [
								submitFrom(model, shared, attempt + 1),
							])
						: finished(model, Option.some(toMessage(error)));
				}
				return again(evo(model, { total: () => result.success.total }), 0, [
					pollFrom(model, shared, 0),
				]);
			},
			CompletedPoll: ({ attempt, generation, result }) => {
				if (generation !== shared.generation || model.step !== "Committing")
					return { model };
				if (attempt !== model.attempt) return { model };
				if (Result.isFailure(result)) {
					const error = result.failure;
					return isRetryable(error) && attempt + 1 < MAX_POLL_FAILURES
						? again(model, attempt + 1, [pollFrom(model, shared, attempt + 1)])
						: finished(model, Option.some(toMessage(error)));
				}
				const status = result.success;
				// The server's list is authoritative: replace, never append.
				const synced = evo(model, {
					results: () => status.results,
					total: () => status.total,
					landed: () => status.landed,
				});
				const reason = Option.fromNullishOr(
					status.stoppedReason ??
						(status.state === "failed" ? status.state : undefined),
				);
				return status.state === "running"
					? again(synced, 0, [pollFrom(synced, shared, 0)])
					: finished(synced, reason);
			},
			ClickedRestart: () => ({ model: initial() }),
			ClickedHistory: () =>
				loadBatches(evo(model, { step: () => "History" as const }), shared),
			CompletedBatches: ({ generation, result }) =>
				generation === shared.generation
					? { model: evo(model, { batches: (data) => settle(data, result) }) }
					: { model },
			ClickedUndo: ({ batchId }) =>
				alongside(
					openDialog(
						evo(model, {
							undoBatchId: () => Option.some(batchId),
							undoPreview: () => AsyncData.Loading(),
							undoError: () => Option.none(),
							undoing: () => false,
						}),
					),
					[FetchUndoPreview({ batchId, generation: shared.generation })],
				),
			CompletedUndoPreview: ({ batchId, generation, result }) =>
				generation === shared.generation &&
				Option.getOrNull(model.undoBatchId) === batchId
					? {
							model: evo(model, {
								undoPreview: (data) => settle(data, result),
							}),
						}
					: { model },
			GotDialogMessage: ({ message: dialogMessage }) =>
				foldDialog(model, dialogMessage),
			ClickedConfirmUndo: () =>
				Option.match(model.undoBatchId, {
					onNone: () => ({ model }),
					onSome: (batchId) => ({
						model: evo(model, {
							undoing: () => true,
							undoError: () => Option.none(),
						}),
						commands: [
							UndoBatch({
								batchId,
								cursor: null,
								generation: shared.generation,
							}),
						],
					}),
				}),
			CompletedUndo: ({ batchId, generation, result }) => {
				if (
					generation !== shared.generation ||
					Option.getOrNull(model.undoBatchId) !== batchId
				)
					return { model };
				if (Result.isFailure(result))
					return {
						model: evo(model, {
							undoing: () => false,
							undoError: () => Option.some(toMessage(result.failure)),
						}),
					};
				const { cursor, done } = result.success;
				// Chunked: keep going while the server hands back somewhere to resume.
				if (!done && cursor !== undefined)
					return {
						model,
						commands: [
							UndoBatch({ batchId, cursor, generation: shared.generation }),
						],
					};
				// Done: shut the dialog and re-read the list so the row says `undone`.
				const finished = evo(model, {
					undoing: () => false,
					undoBatchId: () => Option.none(),
					batches: (data) => start(data),
				});
				return alongside(closeDialog(finished), [
					FetchBatches({ generation: shared.generation }),
				]);
			},
		}),
};
